import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, Segmented, Tag } from 'antd';
import { ShoppingCartOutlined, TeamOutlined, AppstoreOutlined, FileTextOutlined, ExclamationCircleOutlined, DollarOutlined, WarningOutlined, FundOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import * as api from '../api';
import { useTheme } from '../contexts/ThemeContext';
// Trigger re-compilation after dependency installation

const { Title } = Typography;

const Dashboard = () => {
  const { theme } = useTheme();
  const [dashboardData, setDashboardData] = useState({
    totalProducts: 0,
    totalSuppliers: 0,
    totalCustomers: 0,
    totalInventoryValue: 0,
    overdueInvoiceCount: 0,
    lowStockProducts: [],
    recentOrders: []
  });
  const [loading, setLoading] = useState(true);
  const [salesTrend, setSalesTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendPeriod, setTrendPeriod] = useState('month'); // week, month, quarter, year
  const [orderProgress, setOrderProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  // 独立获取销售趋势数据
  const fetchSalesTrend = useCallback(async (period) => {
    setTrendLoading(true);
    try {
      const now = dayjs();
      const trendMap = {};
      let days, startDate, endDate, dateFormat;
      
      // 根据时间范围设置参数
      switch(period) {
        case 'week':
          days = 7;
          startDate = now.subtract(6, 'days').format('YYYY-MM-DD');
          endDate = now.format('YYYY-MM-DD');
          dateFormat = 'MM-DD';
          break;
        case 'month':
          days = 30;
          startDate = now.subtract(29, 'days').format('YYYY-MM-DD');
          endDate = now.format('YYYY-MM-DD');
          dateFormat = 'MM-DD';
          break;
        case 'quarter':
          days = 90;
          startDate = now.subtract(89, 'days').format('YYYY-MM-DD');
          endDate = now.format('YYYY-MM-DD');
          dateFormat = 'MM-DD';
          break;
        case 'year':
          days = 365;
          startDate = now.subtract(364, 'days').format('YYYY-MM-DD');
          endDate = now.format('YYYY-MM-DD');
          dateFormat = 'YYYY-MM';
          break;
        default:
          days = 30;
          startDate = now.subtract(29, 'days').format('YYYY-MM-DD');
          endDate = now.format('YYYY-MM-DD');
          dateFormat = 'MM-DD';
      }
      
      // 初始化日期映射
      for (let i = 0; i < days; i++) {
        const dateKey = now.subtract(days - 1 - i, 'days').format('YYYY-MM-DD');
        trendMap[dateKey] = {
          fullDate: dateKey,
          displayDate: now.subtract(days - 1 - i, 'days').format(dateFormat),
          amount: 0
        };
      }
      
      // 获取销售数据
      const salesResponse = await api.reportAPI.getSalesReport(startDate, endDate);
      
      if (salesResponse && salesResponse.success) {
        const reportData = salesResponse.data || {};
        const orders = reportData.orders || [];
        
        orders.forEach(order => {
          const rawDate = order.orderDate || order.createdTime;
          if (rawDate) {
            const orderDateStr = dayjs(rawDate).format('YYYY-MM-DD');
            if (trendMap[orderDateStr]) {
              const amount = parseFloat(order.finalAmount || 0);
              trendMap[orderDateStr].amount += amount;
            }
          }
        });
      }
      
      // 如果是年度视图，按月聚合数据
      if (period === 'year') {
        const monthMap = {};
        Object.values(trendMap).forEach(item => {
          const monthKey = dayjs(item.fullDate).format('YYYY-MM');
          if (!monthMap[monthKey]) {
            monthMap[monthKey] = {
              fullDate: monthKey,
              displayDate: monthKey,
              amount: 0
            };
          }
          monthMap[monthKey].amount += item.amount;
        });
        const trend = Object.keys(monthMap).sort().map(key => monthMap[key]);
        setSalesTrend(trend);
      } else {
        const trend = Object.keys(trendMap).sort().map(key => trendMap[key]);
        setSalesTrend(trend);
      }
    } catch (error) {
      console.error('获取销售趋势失败:', error);
      setSalesTrend([]);
    } finally {
      setTrendLoading(false);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // 并行获取所有数据，单个失败不影响其他
      const [productsResponse, suppliersResponse, customersResponse, lowStockResponse,
             purchaseOrdersResponse, salesOrdersResponse] = await Promise.all([
        api.productAPI.getProducts({ page: 0, size: 1 }).catch(() => ({ success: false, data: [], totalItems: 0 })),
        api.supplierAPI.getSuppliers({ page: 0, size: 10000 }).catch(() => ({ success: false, data: [], totalItems: 0 })),
        api.customerAPI.getCustomers({ page: 0, size: 10000 }).catch(() => ({ success: false, data: [], totalItems: 0 })),
        api.productAPI.getLowStockProducts().catch(() => ({ success: false, data: [] })),
        api.purchaseOrderAPI.getPurchaseOrders({ page: 0, size: 3 }).catch(() => ({ success: false, data: [] })),
        api.salesOrderAPI.getSalesOrders({ page: 0, size: 3 }).catch(() => ({ success: false, data: [] })),
      ]);

      const totalProducts = productsResponse.totalItems || 0;
      const totalSuppliers = suppliersResponse.totalItems || 0;
      const totalCustomers = customersResponse.totalItems || 0;
      const suppliersList = suppliersResponse.data || [];
      const customersList = customersResponse.data || [];
      const lowStockProducts = (lowStockResponse.data?.content || lowStockResponse.data || []).slice(0, 5);
      const purchaseOrders = purchaseOrdersResponse.data || [];
      const salesOrders = salesOrdersResponse.data || [];
      
      // 为订单添加客户/供应商名称
      const purchaseOrdersWithNames = purchaseOrders.map(order => {
        const supplier = suppliersList.find(s => s.id === order.supplierId);
        return {
          ...order,
          orderType: 'purchase',
          supplierName: supplier ? supplier.supplierName : '未知供应商'
        };
      });
      
      const salesOrdersWithNames = salesOrders.map(order => {
        const customer = customersList.find(c => c.id === order.customerId);
        return {
          ...order,
          orderType: 'sales',
          customerName: customer ? customer.customerName : '未知客户'
        };
      });
      
      // 合并订单并按时间排序（最新5条）
      const allOrders = [
        ...purchaseOrdersWithNames,
        ...salesOrdersWithNames
      ].sort((a, b) => {
        const dateA = new Date(a.orderDate || a.createdTime);
        const dateB = new Date(b.orderDate || b.createdTime);
        return dateB - dateA;
      }).slice(0, 5);
      
      // 获取库存总值（使用报表API）
      let totalInventoryValue = 0;
      try {
        const inventoryReport = await api.reportAPI.getInventoryReport();
        if (inventoryReport.success && inventoryReport.data) {
          totalInventoryValue = inventoryReport.data.totalValue || 0;
        }
      } catch (error) {
        console.error('获取库存总值失败:', error);
      }

      // 获取超期进票数量
      let overdueInvoiceCount = 0;
      try {
        const overdueResponse = await api.purchaseInvoiceAPI.getOverdueTracking();
        if (overdueResponse && overdueResponse.success) {
          overdueInvoiceCount = overdueResponse.totalItems || overdueResponse.data?.length || 0;
        }
      } catch (error) {
        console.error('获取超期进票数量失败:', error);
      }
      
      setDashboardData({
        totalProducts,
        totalSuppliers,
        totalCustomers,
        totalInventoryValue,
        overdueInvoiceCount,
        lowStockProducts,
        recentOrders: allOrders
      });
    } catch (error) {
      console.error('获取仪表板数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取订单进度统计
  const fetchOrderProgress = useCallback(async () => {
    setProgressLoading(true);
    try {
      const response = await api.reportAPI.getOrderProgress();
      if (response && response.success) {
        setOrderProgress(response.data);
      }
    } catch (error) {
      console.error('获取订单进度数据失败:', error);
    } finally {
      setProgressLoading(false);
    }
  }, []);
    
  useEffect(() => {
    fetchDashboardData();
    fetchOrderProgress();
  }, [fetchDashboardData, fetchOrderProgress]);
  
  // 加载销售趋势
  useEffect(() => {
    fetchSalesTrend(trendPeriod);
  }, [trendPeriod, fetchSalesTrend]);
  
  // 处理时间范围切换
  const handlePeriodChange = (value) => {
    setTrendPeriod(value);
  };

  // ========== 订单进度图表数据处理 ==========
  const STATUS_COLORS = ['#faad14', '#52c41a', '#13c2c2', '#237804'];
  const SETTLEMENT_COLORS = ['#52c41a', '#73d13d', '#722ed1', '#eb2f96', '#ff4d4f'];

  const salesStatusMap = { 1: '待审核', 2: '已审核', 3: '已出库', 4: '已完成' };
  const purchaseStatusMap = { 1: '待审核', 2: '已审核', 3: '已入库', 4: '已完成' };
  const settlementMethodMap = {
    cash: '现金', bank: '网银', wechat: '微信', alipay: '支付宝', prepaid: '储值余额', unsettled: '未结算'
  };
  const purchaseSettlementMap = { settled: '已结算', unsettled: '未结算' };

  const buildStatusChartData = (statusList, statusMap) => {
    if (!statusList) return [];
    return statusList.map(item => ({
      name: statusMap[item.status] || `状态${item.status}`,
      value: Number(item.count)
    }));
  };

  const buildSettlementChartData = (list, nameMap, key = 'method') => {
    if (!list) return [];
    // 合并处理：NULL/空串/非法值统一归为「未结算」，避免出现「未知」分类
    const merged = new Map();
    list.forEach((item) => {
      const raw = item[key];
      const normalizedKey = (raw == null || String(raw).trim() === '') ? 'unsettled' : raw;
      const displayName = nameMap[normalizedKey] || nameMap['unsettled'] || '未结算';
      const count = Number(item.count) || 0;
      merged.set(displayName, (merged.get(displayName) || 0) + count);
    });
    return Array.from(merged.entries()).map(([name, value]) => ({ name, value }));
  };

  const salesProgressData = orderProgress ? buildStatusChartData(orderProgress.salesProgress?.statusList, salesStatusMap) : [];
  const purchaseProgressData = orderProgress ? buildStatusChartData(orderProgress.purchaseProgress?.statusList, purchaseStatusMap) : [];
  const salesSettlementData = orderProgress ? buildSettlementChartData(orderProgress.salesSettlement, settlementMethodMap) : [];
  const purchaseSettlementData = orderProgress ? buildSettlementChartData(orderProgress.purchaseSettlement, purchaseSettlementMap) : [];

  const renderPieChart = (data, colors) => (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={40}
          outerRadius={70}
          paddingAngle={3}
          dataKey="value"
          label={({ name, value, percent }) => `${name}: ${value}`}
          labelLine={{ length: 10, length2: 5 }}
          style={{ fontSize: 12 }}
        >
          {data.map((entry, index) => (
            <Cell key={entry.name} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value, name) => [`${value} 单`, name]} />
      </PieChart>
    </ResponsiveContainer>
  );
   




  // 统计卡片配置
  const statCards = [
    {
      title: '商品总数', value: dashboardData.totalProducts, icon: <AppstoreOutlined />,
      color: theme.primaryDark, borderColor: theme.primary, bg: `linear-gradient(135deg, ${theme.primaryBg} 0%, #fff 100%)`,
      link: '/products'
    },
    {
      title: '供应商数量', value: dashboardData.totalSuppliers, icon: <TeamOutlined />,
      color: theme.primaryDark, borderColor: theme.primary, bg: `linear-gradient(135deg, ${theme.primaryBg} 0%, #fff 100%)`,
      link: '/suppliers'
    },
    {
      title: '客户数量', value: dashboardData.totalCustomers, icon: <TeamOutlined />,
      color: '#722ed1', borderColor: '#722ed1', bg: 'linear-gradient(135deg, #f9f0ff 0%, #fff 100%)',
      link: '/customers'
    },
    {
      title: '库存总值', value: dashboardData.totalInventoryValue || 0, icon: <DollarOutlined />,
      color: '#13c2c2', borderColor: '#13c2c2', bg: 'linear-gradient(135deg, #e6fffb 0%, #fff 100%)',
      link: '/inventory', prefix: '¥', precision: 2
    },
    {
      title: '低库存预警', value: (dashboardData.lowStockProducts || []).length, suffix: '种',
      icon: <WarningOutlined />,
      color: (dashboardData.lowStockProducts || []).length > 0 ? '#ff4d4f' : theme.primary,
      borderColor: (dashboardData.lowStockProducts || []).length > 0 ? '#ff4d4f' : theme.primary,
      bg: (dashboardData.lowStockProducts || []).length > 0 ? 'linear-gradient(135deg, #fff2f0 0%, #fff 100%)' : `linear-gradient(135deg, ${theme.primaryBg} 0%, #fff 100%)`,
      link: '/inventory'
    },
    {
      title: '超期进票', value: dashboardData.overdueInvoiceCount, suffix: '单',
      icon: <ExclamationCircleOutlined />,
      color: dashboardData.overdueInvoiceCount > 0 ? '#ff4d4f' : theme.primary,
      borderColor: dashboardData.overdueInvoiceCount > 0 ? '#ff4d4f' : theme.primary,
      bg: dashboardData.overdueInvoiceCount > 0 ? 'linear-gradient(135deg, #fff2f0 0%, #fff 100%)' : `linear-gradient(135deg, ${theme.primaryBg} 0%, #fff 100%)`,
      link: '/purchase-invoices'
    },
  ];

  return (
    <div style={{ padding: '24px', background: theme.primaryBg, minHeight: '100%' }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <FundOutlined style={{ fontSize: 28, color: theme.primary }} />
        <Title level={2} style={{ margin: 0 }}>业务看板</Title>
      </div>
      
      {/* 核心指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map((card, idx) => (
          <Col span={4} key={idx}>
            <Link to={card.link} style={{ display: 'block' }}>
              <Card
                hoverable
                style={{
                  borderTop: `3px solid ${card.borderColor}`,
                  background: card.bg,
                  borderRadius: 8,
                }}
                bodyStyle={{ padding: '20px 16px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minHeight: 62 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 8 }}>{card.title}</div>
                    <Statistic
                      value={card.value}
                      suffix={card.suffix}
                      prefix={card.prefix}
                      precision={card.precision}
                      valueStyle={{ color: card.color, fontSize: 28, fontWeight: 600 }}
                    />
                  </div>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: `${card.borderColor}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, color: card.color, flexShrink: 0
                  }}>
                    {card.icon}
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {/* 订单进度跟踪图表 - 4图一行 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card
            title={<span style={{ fontSize: 14 }}>销售处理进度</span>}
            extra={<Link to="/sales-orders" style={{ fontSize: 12 }}>管理</Link>}
            loading={progressLoading}
            style={{ borderRadius: 8 }}
            bodyStyle={{ padding: '12px 8px' }}
          >
            {salesProgressData.length > 0 ? (
              renderPieChart(salesProgressData, STATUS_COLORS)
            ) : (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography.Text type="secondary">暂无数据</Typography.Text>
              </div>
            )}
            {orderProgress?.salesProgress?.cancelled > 0 && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <Tag color="default" style={{ fontSize: 11 }}>已取消：{orderProgress.salesProgress.cancelled} 单</Tag>
              </div>
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card
            title={<span style={{ fontSize: 14 }}>采购处理进度</span>}
            extra={<Link to="/purchase-orders" style={{ fontSize: 12 }}>管理</Link>}
            loading={progressLoading}
            style={{ borderRadius: 8 }}
            bodyStyle={{ padding: '12px 8px' }}
          >
            {purchaseProgressData.length > 0 ? (
              renderPieChart(purchaseProgressData, STATUS_COLORS)
            ) : (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography.Text type="secondary">暂无数据</Typography.Text>
              </div>
            )}
            {orderProgress?.purchaseProgress?.cancelled > 0 && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <Tag color="default" style={{ fontSize: 11 }}>已取消：{orderProgress.purchaseProgress.cancelled} 单</Tag>
              </div>
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card
            title={<span style={{ fontSize: 14 }}>销售结算进度</span>}
            extra={<Link to="/sales-orders" style={{ fontSize: 12 }}>管理</Link>}
            loading={progressLoading}
            style={{ borderRadius: 8 }}
            bodyStyle={{ padding: '12px 8px' }}
          >
            {salesSettlementData.length > 0 ? (
              renderPieChart(salesSettlementData, SETTLEMENT_COLORS)
            ) : (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography.Text type="secondary">暂无数据</Typography.Text>
              </div>
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card
            title={<span style={{ fontSize: 14 }}>采购结算进度</span>}
            extra={<Link to="/purchase-orders" style={{ fontSize: 12 }}>管理</Link>}
            loading={progressLoading}
            style={{ borderRadius: 8 }}
            bodyStyle={{ padding: '12px 8px' }}
          >
            {purchaseSettlementData.length > 0 ? (
              renderPieChart(purchaseSettlementData, SETTLEMENT_COLORS)
            ) : (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography.Text type="secondary">暂无数据</Typography.Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            style={{ borderRadius: 8 }}
            title="销售趋势"
            extra={
              <Segmented
                options={[
                  { label: '近一周', value: 'week' },
                  { label: '近一月', value: 'month' },
                  { label: '近三月', value: 'quarter' },
                  { label: '近一年', value: 'year' }
                ]}
                value={trendPeriod}
                onChange={handlePeriodChange}
              />
            }
          >
            <div style={{ height: '300px', width: '100%' }}>
              {trendLoading ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spin />
                </div>
              ) : salesTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={salesTrend}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="displayDate" />
                    <YAxis tickFormatter={(value) => `¥${value}`} />
                    <Tooltip 
                      formatter={(value) => [`¥${parseFloat(value).toLocaleString()}`, '销售额']}
                      labelFormatter={(label, payload) => {
                        const item = payload && payload[0] ? payload[0].payload : {};
                        return `日期: ${item.fullDate || label}`;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="amount" name="销售额" fill={theme.primary} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography.Text type="secondary">暂无销售数据</Typography.Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;